import Fuse from 'fuse.js';
import { type TFunction } from 'i18next';
import { MonitorIcon, PlugIcon } from 'lucide-react';

import { integrationIconByType } from '@/components/atoms/Icons/integrationIconByType';
import { type Desks } from '@/hooks/useDesks';
import { type Desk } from '@/shared/types/desk';
import { type IntegrationType } from '@/shared/types/integration';

import { type EntityResult, type IntegrationSearchItem } from '../../types';
import { MAX_PER_TYPE } from '../../utils';

/**
 * Searches environments (desks) and their integrations using pre-built Fuse instances.
 */
export const searchDesks = (
  query: string,
  desksFuse: Fuse<Desk> | null,
  integrationFuse: Fuse<IntegrationSearchItem> | null,
  desksData: Desks | undefined,
  t: TFunction
): EntityResult[] => {
  if (!desksData?.desks) {
    return [];
  }

  const results: EntityResult[] = [];

  // Search Environments (desks) by name using passed Fuse instance
  const deskResults = desksFuse ? desksFuse.search(query, { limit: MAX_PER_TYPE }) : [];

  // Convert desk matches to EntityResults
  const deskMatches = deskResults.map(({ item: d, score }) => ({
    id: d.desk_id,
    name: d.name,
    type: 'environment' as const,
    path: d.is_service_desk
      ? `/chats/${d.desk_id}/conversations`
      : `/environments/${d.desk_id}/integrations`,
    icon: MonitorIcon,
    subtitle: d.is_service_desk ? t('common.conversations') : undefined,
    score: 1 - (score ?? 0),
  }));
  results.push(...deskMatches);

  // Search Integrations using passed Fuse instance
  const integrationResults = integrationFuse
    ? integrationFuse
        .search(query, { limit: MAX_PER_TYPE })
        .map(({ item, score }) => ({
          id: item.integration.integration_id,
          name: item.label,
          type: 'integration' as const,
          path: `/environments/${item.desk.desk_id}/integrations`,
          icon: integrationIconByType[item.integration.type as IntegrationType] ?? PlugIcon,
          subtitle: item.desk.name,
          score: 1 - (score ?? 0),
        }))
    : [];

  results.push(...integrationResults);

  return results;
};
